import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  Cell,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AssessmentReport, ScoreResult } from '../../../support/assessmentScoring';

/**
 * The computed summary of a filled-in maturity assessment: a headline stage, a per-domain radar,
 * per-pillar bars, and a control-by-control breakdown. Presentational and pure — it takes a
 * `ScoreResult` and nothing else, so it renders the same for the respondent's post-submit screen
 * and the admin's review of a stored response set.
 *
 * `dark` swaps the palette for the full-page "command-center" theme; the default light palette suits
 * the admin review inside the normal chrome.
 */

type Props = {
  result: ScoreResult;
  /** The narrative report (stage prose + focus areas), when the assessment carries report content. */
  report?: AssessmentReport | null;
  /** Stage 1..N maps to a colour ramp red→green. */
  dark?: boolean;
};

const STAGE_COLORS = ['#cf2030', '#ff9000', '#ffcc00', '#6cc04a', '#02c8ff'];

const stageColor = (stage: number) => STAGE_COLORS[Math.max(0, Math.min(STAGE_COLORS.length - 1, stage - 1))];

const AssessmentResultsSummary: React.FC<Props> = ({ result, report, dark }) => {
  const ink = dark ? '#f1f5fb' : undefined;
  const inkMuted = dark ? '#9fb3cc' : 'text.secondary';
  const surface = dark ? 'rgba(16,31,56,0.7)' : undefined;
  const grid = dark ? 'rgba(159,179,204,0.25)' : '#d0d7de';

  const radarData = result.domains.map((d) => ({
    domain: d.domain,
    label: d.label,
    mean: Number(d.mean.toFixed(2)),
  }));

  const controlData = result.controls.map((c) => ({
    key: c.key,
    level: c.level ?? 0,
    answered: c.level != null,
  }));

  const axisMax = 4;

  return (
    <Stack spacing={3}>
      {/* Headline stage */}
      <Paper variant="outlined" sx={{ p: 3, bgcolor: surface, borderColor: grid }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="overline" sx={{ color: inkMuted }}>
              Program maturity
            </Typography>
            <Typography variant="h4" sx={{ color: ink, fontWeight: 700 }}>
              Stage {result.stage}: {result.stageName}
            </Typography>
            <Typography variant="body2" sx={{ color: inkMuted, mt: 0.5 }}>
              Weighted maturity {result.overallMean.toFixed(2)} / {axisMax} · {result.answered} of{' '}
              {result.total} controls answered
            </Typography>
          </Box>
          <Box
            sx={{
              width: 84,
              height: 84,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              fontSize: 32,
              fontWeight: 800,
              bgcolor: stageColor(result.stage),
              boxShadow: dark ? `0 0 24px ${stageColor(result.stage)}66` : undefined,
            }}
          >
            {result.stage}
          </Box>
        </Box>
        {result.cappedBy && (
          <Chip
            size="small"
            sx={{ mt: 1.5 }}
            color="warning"
            variant={dark ? 'filled' : 'outlined'}
            label={
              result.cappedBy === 'governance'
                ? 'Capped by governance domain (a weak governance pillar limits the overall stage)'
                : result.cappedBy === 'weakest_pillar'
                ? 'Capped by weakest pillar (no domain may be more than one stage ahead of the weakest)'
                : 'Capped by governance and weakest pillar'
            }
          />
        )}
      </Paper>

      {/* Narrative report: stage prose + where to focus / what to sustain */}
      {report && (report.narrative || report.focusAreas.length > 0) && (
        <Paper variant="outlined" sx={{ p: 3, bgcolor: surface, borderColor: grid }}>
          {report.narrative && (
            <Typography variant="body1" sx={{ color: dark ? '#cdd9ea' : undefined, mb: report.focusAreas.length ? 2.5 : 0 }}>
              {report.narrative}
            </Typography>
          )}

          {report.focusAreas.length > 0 && (
            <Box>
              <Typography variant="overline" sx={{ color: inkMuted }}>
                Priority focus areas
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {report.focusAreas.map((item) => (
                  <Box
                    key={item.domain}
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      borderLeft: `3px solid ${stageColor(item.stage)}`,
                      bgcolor: dark ? 'rgba(2,200,255,0.06)' : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="subtitle2" sx={{ color: ink }}>
                        {item.label}
                      </Typography>
                      <Chip size="small" label={`Stage ${item.stage}: ${item.stageName}`} variant="outlined" />
                    </Box>
                    {item.recommendation && (
                      <Typography variant="body2" sx={{ color: inkMuted, mt: 0.5 }}>
                        {item.recommendation}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {report.strengths.length > 0 && (
            <Box mt={2.5}>
              <Typography variant="overline" sx={{ color: inkMuted }}>
                Strengths to sustain
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {report.strengths.map((item) => (
                  <Box key={item.domain} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={`${item.label} · Stage ${item.stage}`}
                      sx={{ bgcolor: dark ? 'rgba(108,192,74,0.15)' : undefined }}
                    />
                    {item.recommendation && (
                      <Typography variant="body2" sx={{ color: inkMuted }}>
                        {item.recommendation}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
        {/* Per-domain radar */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: surface, borderColor: grid }}>
          <Typography variant="subtitle1" sx={{ color: ink, mb: 1 }}>
            Maturity by domain
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData} outerRadius="70%">
              <PolarGrid stroke={grid} />
              <PolarAngleAxis dataKey="domain" tick={{ fill: dark ? '#9fb3cc' : '#57606a', fontSize: 12 }} />
              <PolarRadiusAxis domain={[0, axisMax]} tick={{ fill: dark ? '#9fb3cc' : '#57606a', fontSize: 10 }} />
              <Radar dataKey="mean" stroke="#02c8ff" fill="#02c8ff" fillOpacity={0.35} />
              <ReTooltip
                formatter={(value) => [`${value} / ${axisMax}`, 'Mean maturity']}
                labelFormatter={(label) =>
                  radarData.find((d) => d.domain === label)?.label || label
                }
              />
            </RadarChart>
          </ResponsiveContainer>
        </Paper>

        {/* Per-pillar bars */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: surface, borderColor: grid }}>
          <Typography variant="subtitle1" sx={{ color: ink, mb: 1 }}>
            Stage by pillar
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={result.domains} margin={{ top: 8, right: 8, bottom: 8, left: -20 }}>
              <XAxis dataKey="domain" tick={{ fill: dark ? '#9fb3cc' : '#57606a', fontSize: 12 }} />
              <YAxis domain={[0, 5]} allowDecimals={false} tick={{ fill: dark ? '#9fb3cc' : '#57606a', fontSize: 11 }} />
              <ReTooltip
                formatter={(value) => [`Stage ${value}`, 'Pillar stage']}
                labelFormatter={(label) =>
                  result.domains.find((d) => d.domain === label)?.label || label
                }
              />
              <Bar dataKey="stage" radius={[4, 4, 0, 0]}>
                {result.domains.map((d) => (
                  <Cell key={d.domain} fill={stageColor(d.stage)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Control-by-control */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: surface, borderColor: grid }}>
        <Typography variant="subtitle1" sx={{ color: ink, mb: 1 }}>
          Control-by-control maturity
        </Typography>
        <ResponsiveContainer width="100%" height={Math.max(220, controlData.length * 22)}>
          <BarChart
            layout="vertical"
            data={controlData}
            margin={{ top: 8, right: 16, bottom: 8, left: 24 }}
          >
            <XAxis
              type="number"
              domain={[0, axisMax]}
              allowDecimals={false}
              tick={{ fill: dark ? '#9fb3cc' : '#57606a', fontSize: 11 }}
            />
            <YAxis
              type="category"
              dataKey="key"
              width={90}
              tick={{ fill: dark ? '#9fb3cc' : '#57606a', fontSize: 11 }}
            />
            <ReTooltip
              formatter={(value, _n, entry) =>
                entry?.payload?.answered ? [`Level ${value}`, 'Maturity'] : ['Not answered', 'Maturity']
              }
            />
            <Bar dataKey="level" radius={[0, 4, 4, 0]}>
              {controlData.map((c) => (
                <Cell key={c.key} fill={c.answered ? stageColor(c.level) : (dark ? '#2a3a55' : '#e1e4e8')} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Stack>
  );
};

export default AssessmentResultsSummary;
